import { useEffect, useMemo, useRef, useState } from 'react'
import { browseDirectory, type BrowseNode } from './browse'
import { isWindowsPath, joinPath, lastSep, sepFor, trimSep } from './machinePath'

/**
 * ONE LINE for the folder, replacing a browser that was five.
 *
 *   owner: 基本上你這麼多排, 我只需要一排就可以解決 — [text input starting from home]
 *          點擊時跳出下拉,顯示沒有被隱藏的 folder;打字即時 filter;點選直接更新
 *          文字;完全沒對應到任何 folder 就代表要新建,並且可以是 multi layer
 *
 * WHAT WAS WRONG WITH THE OLD ONE, and it is not only the height: it was a SECOND
 * place to answer the same question. A text field said where you were going, a
 * separate list said where you were, an up-arrow moved one of them and not the other,
 * and a "create folder" box beside them made a third kind of answer. Four controls for
 * one string, and the string was already in the first of them.
 *
 * So the text IS the state. Everything else is an aid to typing it:
 *
 *   - the list shows what exists in the directory the text currently NAMES, so it
 *     follows the caret rather than being navigated;
 *   - typing after the last `/` filters that list;
 *   - picking a row completes the text and opens the next level, because the next
 *     thing someone types is almost always the next segment;
 *   - text matching nothing that exists is a folder to CREATE, said out loud, and it
 *     can be several levels deep because `mkdir -p` is what a person means when they
 *     type three segments that are not there.
 */

/** Split "/a/b/frag" into the directory to list and the fragment being typed. */
export function splitPath(text: string): { dir: string; frag: string } {
  // Either separator on a Windows path (machinePath.ts); only `/` elsewhere.
  const cut = lastSep(text)
  if (cut < 0) return { dir: '', frag: text }
  // A trailing slash means the directory itself, with nothing typed yet — the case a
  // naive `slice(cut + 1)` gets right by accident and a `split('/').pop()` does not.
  const dir = text.slice(0, cut)
  // A drive's root keeps its separator: `C:\` lists the drive, `C:` would not.
  if (isWindowsPath(text) && /^[A-Za-z]:$/.test(dir)) return { dir: `${dir}\\`, frag: text.slice(cut + 1) }
  return { dir: dir || '/', frag: text.slice(cut + 1) }
}

/**
 * Which entries to offer for what has been typed.
 *
 * Case-insensitive PREFIX, not substring: a person typing `Do` means Documents and
 * Downloads, not every folder with an o in it. Hidden entries stay hidden until the
 * fragment starts with a dot — the owner asked for "沒有被隱藏的 folder", and the
 * exception is that typing `.` is an unambiguous request for them.
 */
export function matchEntries(nodes: BrowseNode[], frag: string): BrowseNode[] {
  const f = frag.toLowerCase()
  const wantsHidden = frag.startsWith('.')
  return nodes.filter((n) => {
    if (!wantsHidden && n.name.startsWith('.')) return false
    return n.name.toLowerCase().startsWith(f)
  })
}

/**
 * The segments to create, relative to the deepest directory that actually listed.
 *
 * Multi-level is the case this exists for. Typing `~/a/b/c` where `a` is missing makes
 * the directory-to-list `~/a/b`, which cannot be listed at all — so the component sees
 * an error and one unjudged fragment. Reporting just `[c]` would `mkdir` one level
 * under a parent that is not there; the honest answer is everything below the last
 * directory the machine could show us.
 */
export function missingUnder(listedPath: string, dir: string, frag: string): string[] {
  // On Windows both separators are one: compared with `/`, then split on it.
  const norm = (p: string) => (isWindowsPath(listedPath) || isWindowsPath(dir) ? p.replace(/\\/g, '/') : p)
  const base = norm(listedPath).replace(/\/+$/, '')
  const full = norm(`${dir}/${frag}`).replace(/\/{2,}/g, '/')
  const rest = full.startsWith(`${base}/`) ? full.slice(base.length + 1) : norm(frag)
  return rest.split('/').filter(Boolean)
}

/** Does the typed text name something that exists? Drives the create-vs-use message. */
export function exactMatch(nodes: BrowseNode[], frag: string): boolean {
  return frag !== '' && nodes.some((n) => n.name === frag)
}

export function FolderField({
  deviceId,
  serverName,
  value,
  onChange,
  onMissing,
  locked,
  onUnlock,
}: {
  deviceId: string
  serverName: string
  value: string
  onChange: (path: string, touched: boolean) => void
  /*
   * Which segments of the typed path do NOT exist, and the deepest directory that
   * does. Reported UP rather than recomputed in the sheet, because only this component
   * has the listing — a second computation from the same string would be a second
   * opinion that silently disagrees the moment either side changes.
   */
  onMissing?: (base: string, segments: string[]) => void
  /*
   * NOTHING BUT THE PERSON WRITES HERE WHILE THIS IS SET — owner, 2026-10-13: Face ID
   * filled the vault password in the restore form and the saved account e-mail landed in
   * the folder. A password manager puts its username in a text field before the
   * password; a read-only one it skips, and what it types anyway is dropped. The
   * person's own tap (or focus) asks for the field back through `onUnlock`. The
   * machine's home is still written in: that comes from the listing, not the input.
   */
  locked?: boolean
  onUnlock?: () => void
}) {
  const [open, setOpen] = useState(false)
  /** The live value and whether anyone has edited it — see the prefill effect. */
  const latest = useRef({ value, touched: false })
  latest.current.value = value
  /*
   * Home is READ BACK from the machine's own answer rather than declared anywhere.
   * `browse('~')` returns the absolute `path` it resolved to, so the machine tells us
   * where its sessions start — one source, and it is the same one the listing came
   * from. A second declaration would be a second thing to keep true.
   */
  /**
   * THE MACHINE EVERYTHING BELOW WAS LEARNED FROM.
   *
   * `deviceId` is fixed for the life of the create sheet — one server, one sheet — so
   * for two years nothing here had to survive it changing. The fork form is the first
   * caller where it changes UNDER a live field: pick a different target machine and
   * this component keeps the previous machine's home, its listing, and its "has not
   * answered yet", none of which are true of the new one.
   *
   * The guard in the prefill effect is what made that visible: `if (home) return` saw
   * the OLD machine's home and never asked the new one, so the field stayed empty while
   * the tree below it still showed the folders of a machine the fork was no longer
   * going to — and the create button, which needs a path, stayed disabled.
   */
  const learnedFrom = useRef(deviceId)
  const [home, setHome] = useState<string | null>(null)
  /** Where the CURRENT listing actually came from, as the machine resolved it. */
  const [listedPath, setListedPath] = useState<string | null>(null)
  const [nodes, setNodes] = useState<BrowseNode[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  /**
   * Bumped to re-ask after a failure — owner, 2026-08-13: the picker showed
   * "has not answered yet" and STAYED there. The machine's fs.browse handler
   * had hung; when it recovered the picker did not, because the home fetch
   * runs once (guarded by `if (home) return`, deps `[deviceId]`) and never
   * retries. A transient on the machine must not strand the picker until the
   * whole sheet is closed and reopened — a retry the person can take, since
   * there is no event on the wire that says "the machine is answering again".
   */
  const [reload, setReload] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const { dir, frag } = splitPath(value)

  /*
   * ASK FOR HOME IMMEDIATELY, not on focus, and PUT IT IN THE FIELD.
   *
   *   owner: Folder path 的 editor 裡面應該要放 "Default path" 也就是 home path 的
   *          字串吧. 而不是給一個虛的.
   *
   * It was a placeholder — grey, unselectable, gone the moment anything is typed. A
   * field that SHOWS one thing and SENDS another is the shape this app keeps removing:
   * the screen said "/home/yuanyen" and the request carried `cwd: null`, and the two
   * only agreed because the edge's default happened to be the same directory. Now the
   * text is the value, it is real from the start, and it is what travels.
   *
   * `cwd: null` is still reachable and still means "the edge decides" — by CLEARING
   * the field, which is a thing a person does on purpose rather than a state they were
   * left in.
   */
  useEffect(() => {
    if (learnedFrom.current !== deviceId) {
      learnedFrom.current = deviceId
      setHome(null)
      setListedPath(null)
      setNodes(null)
      setProblem(null)
      /*
       * A path chosen for one machine is not a choice about a different one, and the
       * caller clears the text when the machine changes. Clearing `touched` too is what
       * lets the new machine's home be written in: the prefill below refuses to write
       * into a field somebody has touched, so without this every machine after the
       * first would open blank. It does not weaken the promise that CLEARING stays
       * cleared — that promise is about a field on one machine, and this only ever runs
       * when the question itself has changed.
       */
      latest.current.touched = false
    } else if (home) return
    let cancelled = false
    void browseDirectory(deviceId, '~').then((r) => {
      if (cancelled || r.kind !== 'entries') return
      setHome(r.path)
      setListedPath(r.path)
      setNodes(r.nodes)
      /*
       * ONCE, and only into a field nobody has touched.
       *
       * `value` is read through a ref rather than closed over: this effect runs on
       * mount and the closure would hold the empty string for ever, so a person who
       * cleared the field would have home written straight back — the exact gesture
       * this design just promised means "the edge decides". Clearing has to STAY
       * cleared, or `cwd: null` is unreachable and the promise is decoration.
       *
       * `false` for touched: showing somebody where they are is not them choosing it,
       * and that distinction still decides whether a create counts as deliberate.
       */
      if (!latest.current.value && !latest.current.touched) {
        onChange(`${trimSep(r.path).replace(/[\\/]$/, '')}${sepFor(r.path)}`, false)
      }
    })
    return () => {
      cancelled = true
    }
    // `reload` re-asks for home after a machine that had not answered comes back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, reload])

  /*
   * The listing follows the TEXT, not a cursor the user moves separately. Keyed on
   * `dir` alone, so typing within one directory never re-asks — the filter is local.
   */
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setProblem(null)
    void browseDirectory(deviceId, dir || home || '~').then((r) => {
      if (cancelled) return
      if (r.kind === 'entries') {
        setNodes(r.nodes)
        setListedPath(r.path)
        if (!home) setHome(r.path)
      }
      else {
        setNodes([])
        /*
         * The machine's own words, and the states kept apart: `no-answer` is a machine
         * that did not reply, `unreachable` is one that could not be asked. Collapsing
         * them into "could not list" would describe our disappointment rather than
         * what happened — and only one of the two is worth waiting through.
         */
        setProblem(
          r.kind === 'unreachable'
            ? r.detail
            : r.kind === 'no-answer'
              ? `${serverName} has not answered yet`
              : 'browsing is not wired on this machine',
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [open, dir, deviceId, home, serverName, reload])

  // Close on a click outside — not on blur, which fires before a row's click lands
  // and would make every suggestion unselectable.
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  const shown = useMemo(() => matchEntries(nodes ?? [], frag), [nodes, frag])
  const exists = exactMatch(nodes ?? [], frag)
  /*
   * "This will be created" covers TWO shapes, and excluding the second is what made
   * multi-level silently wrong:
   *
   *   the directory listed and the name is not in it   → one level to create
   *   the directory could NOT be listed                → it does not exist either,
   *                                                      so this and its parents
   *
   * The first version required `!problem`, which is precisely the second case — so a
   * two-level path showed an error instead of an intention, and reported nothing to
   * create. `no-answer` is deliberately NOT included: a machine that has not replied
   * yet is not a machine that has said no.
   */
  const missingParent = problem !== null && nodes !== null && !/has not answered/.test(problem)
  const willCreate = frag !== '' && nodes !== null && (missingParent || shown.length === 0)

  /*
   * WHICH SEGMENTS ARE MISSING, walked back from the deepest directory that listed.
   *
   * The listing is for `dir`, so on its own it can only judge the LAST segment — and
   * multi-level is exactly the case where that is not enough: typing `~/a/b/c` where
   * `a` does not exist makes `dir` = `~/a/b`, which cannot be listed at all. The
   * request comes back unreachable, `frag` is unjudged, and reporting `[c]` would have
   * created one level under a parent that is not there.
   *
   * So the base is the last directory that DID list (`listedPath`), and everything
   * from there down is missing. My first version reported `[frag]` and I only noticed
   * because the screenshot of a two-level path showed `newproj/deep` as one name —
   * a rendering that was honest about a computation that was not.
   */
  const [missingBase, setMissingBase] = useState('')
  const [missingHere, setMissingHere] = useState<string[]>([])

  useEffect(() => {
    if (!willCreate || !listedPath) {
      setMissingBase('')
      setMissingHere([])
      onMissing?.(dir, [])
      return
    }
    const base = trimSep(listedPath)
    const segs = missingUnder(listedPath, dir, frag)
    setMissingBase(base)
    setMissingHere(segs)
    onMissing?.(base, segs)
  }, [willCreate, dir, frag, listedPath, onMissing])

  /** The machine's separator, as its own paths write it (machinePath.ts). */
  const sep = sepFor(listedPath ?? home ?? value)

  const pick = (name: string) => {
    /*
     * THE DIRECTORY THE LISTING CAME FROM, not the one parsed out of the text.
     *
     * On the first open the text is still empty, so `splitPath` yields dir = "" while
     * the LIST on screen is the machine's home — clicking `workspace/` then produced
     * `/workspace/`, an absolute path at the filesystem root that happens to look
     * plausible and does not exist. The listing already knows where it came from
     * (`listedPath`), and that is the only correct base for a row inside it.
     */
    const base = listedPath ?? dir
    onChange(`${joinPath(base, name)}${sepFor(base)}`, true)
    setOpen(true) // the next segment is almost always what gets typed next
  }

  return (
    <div className="ff" ref={box}>
      <input
        className="nas-input"
        /* A STABLE handle. The placeholder becomes the machine's home once it answers,
           so anything that located this field by placeholder text found it before the
           listing resolved and not after — a locator that changes when the data does
           is a race with the network. */
        aria-label={`Folder on ${serverName}`}
        value={value}
        /* Only speaks when the field is genuinely empty — which now means the person
           cleared it, and the sentence is what that choice DOES. */
        placeholder="its default if left empty" 
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        readOnly={locked}
        aria-readonly={locked}
        onPointerDown={() => onUnlock?.()}
        onFocus={() => {
          onUnlock?.()
          setOpen(true)
        }}
        onChange={(e) => {
          if (locked) return
          latest.current.touched = true
          onChange(e.target.value, true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
          // Enter completes the single obvious match rather than submitting the form
          // half-typed — the same key doing two things is what makes a combobox feel
          // like a trap.
          if (e.key === 'Enter' && open && shown.length === 1 && !exists) {
            e.preventDefault()
            pick(shown[0]!.name)
          }
        }}
      />

      {open && (
        <div className="ff-pop" role="listbox" aria-label={`Folders on ${serverName}`}>
          {problem && !willCreate ? (
            <div className="ff-msg ff-err">
              {problem}
              {/* A machine that has not answered may just be slow or mid-restart;
                  the listing has no push behind it, so the person re-asks. */}
              <button
                type="button"
                className="ff-retry"
                onMouseDown={(e) => {
                  // mousedown, not click: the outside-click closer fires on
                  // mousedown and would shut the pop before a click landed.
                  e.preventDefault()
                  setProblem(null)
                  setHome(null)
                  setNodes(null)
                  setReload((n) => n + 1)
                }}
              >
                Retry
              </button>
            </div>
          ) : nodes === null ? (
            <div className="ff-msg">Asking {serverName}…</div>
          ) : willCreate ? (
            /*
             * The create case is a STATEMENT, not a button: the folder is made by
             * Create &amp; launch, with the session, so there is no separate action to
             * take and no way to end up with a folder and no agent in it.
             */
            <div className="ff-msg ff-new">
              {/* Names EVERY level that will be made, not just the last one typed —
                  the sentence and the mkdir now come from the same computation. */}
              <b>{missingHere.join(sep) || frag}</b> does not exist — it will be created
              {missingBase ? ` in ${missingBase}` : ''}.
            </div>
          ) : shown.length === 0 ? (
            <div className="ff-msg">Nothing here.</div>
          ) : (
            shown.map((n) => (
              <button
                key={n.name}
                type="button"
                role="option"
                aria-selected={n.name === frag}
                className={`ff-row${n.mark ? ' ff-deny' : ''}`}
                disabled={!!n.mark}
                onClick={() => pick(n.name)}
              >
                {n.name}
                {sep}
                {/* Reported and unreadable, shown rather than hidden: an entry the
                    machine chose to report and we silently dropped is worse than one
                    that says why it cannot be entered. */}
                {n.mark && <span className="ff-mark">{n.mark}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
